import React, { useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { useI18n } from "@/contexts/I18nContext";

/** A filter choice that stays pressed while active. */
export const FilterChip = ({ active, onClick, children, testid }) => (
  <button
    type="button" onClick={onClick} aria-pressed={active} data-testid={testid}
    className={`rounded-full border px-3.5 py-1.5 text-left leading-snug transition-colors md:w-full md:rounded-[var(--radius)] md:border-transparent md:px-2 md:py-1
      ${active ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50 hover:text-primary"}`}
  >
    {children}
  </button>
);

/**
 * Shared layout for the book, author and publisher lists. Filters are a
 * sidebar from md up and a bottom sheet on phones, so results start on the
 * first screen. Active filters show as removable chips above the results.
 */
export function ListLayout({ title, count, filters, sort, active = [], onRemove, children }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <header className="flex flex-wrap items-end justify-between gap-3 shirorekha pb-3">
        <div>
          <h1 className="text-[2rem] sm:text-[2.4rem]">{title}</h1>
          {count != null && <p className="text-muted-foreground" data-testid="list-count">{count}</p>}
        </div>
        {sort && <div className="hidden md:block">{sort}</div>}
      </header>

      <div className={filters ? "mt-6 md:grid md:grid-cols-[200px_minmax(0,1fr)] md:gap-10" : "mt-6"}>
        {filters && <aside className="hidden md:block" aria-label={t("filters")}>{filters}</aside>}
        <div className="min-w-0">
          {(filters || sort) && (
            <div className="mb-4 flex items-center gap-2 md:hidden">
              {filters && (
                <Drawer open={open} onOpenChange={setOpen}>
                  <DrawerTrigger asChild>
                    <button type="button" className="inline-flex items-center gap-2 rounded-[var(--radius)] border border-input px-3 py-2" data-testid="filters-button">
                      <SlidersHorizontal size={16} aria-hidden="true" />{t("filters")}{active.length > 0 && ` (${active.length})`}
                    </button>
                  </DrawerTrigger>
                  <DrawerContent className="max-h-[85svh]">
                    <DrawerHeader className="text-left"><DrawerTitle>{t("filters")}</DrawerTitle></DrawerHeader>
                    <div className="overflow-y-auto px-4 pb-4">{filters}</div>
                    <div className="border-t border-border p-4">
                      <button type="button" onClick={() => setOpen(false)} className="w-full rounded-[var(--radius)] bg-primary py-2.5 text-primary-foreground">
                        {t("show_results")}
                      </button>
                    </div>
                  </DrawerContent>
                </Drawer>
              )}
              {sort && <div className="ml-auto">{sort}</div>}
            </div>
          )}
          {active.length > 0 && (
            <ul className="mb-4 flex flex-wrap gap-2" aria-label={t("active_filters")}>
              {active.map((f) => (
                <li key={f.key}>
                  <button type="button" onClick={() => onRemove(f.key)} aria-label={`${t("remove_filter")}: ${f.label}`}
                    className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-sm hover:bg-secondary/70">
                    {f.label}<X size={14} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
